export type AudienceProof = {
  client: string;
  videoId: string;
  tag: string;
  impact: string;
};

export type AudienceFAQ = { q: string; a: string };

export type AudienceProcessStep = { title: string; body: string };

export type AudienceLeadMagnet = {
  slug: string;
  title: string;
  desc: string;
  cta: string;
};

export type Audience = {
  slug: "grundare" | "marknadsteam" | "byraer" | "forbund" | "some-team";
  to: string;
  label: string;
  intro: string;
  longIntro: string;
  metric: string;
  pains: string[];
  outcomes: string[];
  recommend: string;
  recommendHref: string;
  quote: { text: string; who: string };
  processSteps: AudienceProcessStep[];
  proof: AudienceProof[];
  leadMagnet: AudienceLeadMagnet;
  faq: AudienceFAQ[];
  /** YouTube videoId shown autoplaying in the hero frame. */
  heroVideoId: string;
  /** Production stills shown in a marquee further down the page. */
  stills: string[];
};

export const audiences: Audience[] = [
  {
    slug: "grundare",
    to: "/for-vem/grundare",
    label: "Grundare",
    intro: "Film som följer ert tempo — innan ni är redo att pitcha.",
    longIntro:
      "Du bygger något nytt och behöver visa traction utan att hyra in ett produktionsbolag på varje milstolpe. Vi paketerar berättelsen så ni kan visa investerare, kunder och kandidater var ni är på väg.",
    metric: "Founder-story + månadsklipp på under 3 veckor — utan att ni rör en kamera",
    pains: [
      "Ingen tid att producera, ändå konstant behov av content",
      "Pitchdeck räcker inte — investerare vill se rörligt",
      "Otydligt vad ni faktiskt ska säga utåt just nu",
    ],
    outcomes: [
      "En tydlig founderberättelse i flera format",
      "Klipp till LinkedIn varje vecka utan att ni rör en kamera",
      "Material att åter­använda i pitch, sales och rekrytering",
    ],
    recommend: "Pulse Core",
    recommendHref: "/pulse",
    quote: {
      text: "Vi ser ut som ett bolag dubbelt så stort utan att anställa.",
      who: "Founder, SaaS-bolag",
    },
    processSteps: [
      {
        title: "1. Strategimöte (30 min)",
        body: "Vi går igenom var ni är, vart ni ska och vilken berättelse som öppnar dörrar nu — för investerare, kunder eller kandidater.",
      },
      {
        title: "2. Foundation shoot (1 dag)",
        body: "En inspelningsdag som ger råmaterial för 3–6 månader. Founderintervju, miljöer, produktbilder. Vi regisserar, ni dyker upp.",
      },
      {
        title: "3. Månatlig leverans",
        body: "Klippt material varje månad: ett pitchklipp, sociala cutdowns, ett rekryteringsklipp. Allt i ert tonalitet.",
      },
    ],
    proof: [
      {
        client: "Bolt",
        videoId: "yvojXrb41Aw",
        tag: "Pulse · Performance",
        impact: "Snabb, igenkännbar pitchfilm för paid social när varje sekund räknas.",
      },
      {
        client: "Garageportexperten",
        videoId: "9iiyNS4yNzA",
        tag: "Prime · Brand",
        impact: "Brand storytelling som blivit ryggraden i pitch, on-boarding och rekrytering.",
      },
    ],
    leadMagnet: {
      slug: "grundare-pitchfilm-mall",
      title: "Pitchfilm-mall för founders",
      desc: "10 sektioner som investerare faktiskt vill se i en 90-sekunders pitchfilm — med exempelmanus och hooks.",
      cta: "Skicka mig mallen",
    },
    faq: [
      {
        q: "Vi är pre-seed — är ni inte för dyra?",
        a: "Pulse Core är prissatt för bolag som inte vill rekrytera en in-house contentperson. Räkna det mot en månadslön så ser det rimligt ut snabbt.",
      },
      {
        q: "Vi vet inte vad vi vill säga än.",
        a: "Det är precis därför vi börjar med strategimötet. Vi har gjort det här åt 60+ founders och hjälper er hitta vinkeln innan vi tar fram kameran.",
      },
      {
        q: "Vad behöver vi förbereda?",
        a: "Inget mer än 30 minuter för briefing och en halvdag för inspelning. Vi driver manus, regi, klipp och leverans.",
      },
      {
        q: "Får vi äga materialet?",
        a: "Ja. All färdig film tillhör er — vi tar inte rättigheter, ingen royalty.",
      },
    ],
    heroVideoId: "9iiyNS4yNzA",
    stills: [
      "/prime-stills/garageportexperten-fenny-1.jpg",
      "/prime-stills/recompany-1.jpg",
      "/prime-stills/garageportexperten-fenny-5.jpg",
      "/prime-stills/fenny-reklam-film-1.jpg",
      "/prime-stills/recompany-2.jpg",
      "/prime-stills/garageportexperten-fenny-2.jpg",
      "/prime-stills/fenny-reklam-film-7.jpg",
      "/prime-stills/garageportexperten-fenny-6.jpg",
    ],
  },
  {
    slug: "marknadsteam",
    to: "/for-vem/marknadsteam",
    label: "Marknadsteam",
    intro: "Avlasta interna teamet och lansera kampanjer snabbare.",
    longIntro:
      "Ni vet exakt vad ni vill ha — men interna resurser räcker inte. Vi blir er externa filmavdelning som producerar i takten ni planerar för, inte i den takt en byrå råkar ha kapacitet.",
    metric: "Från brief till publicerat klipp på under två veckor — varje månad",
    pains: [
      "Byråoffert tar 3–6 veckor — kampanjfönstret är borta då",
      "Interna teamet hinner inte med både strategi och produktion",
      "Skalbarhet saknas när kampanjer drar igång samtidigt",
    ],
    outcomes: [
      "Förutsägbar leveranskadens varje månad",
      "Flera hooks per kampanj att A/B-testa",
      "En partner som förstår er brandbook från dag ett",
    ],
    recommend: "Pulse Pro",
    recommendHref: "/pulse",
    quote: {
      text: "Vi går från brief till publicerat klipp på under två veckor.",
      who: "Marknadschef, scaleup",
    },
    processSteps: [
      {
        title: "1. Onboarding-vecka",
        body: "Vi äter er brandbook till frukost. Tonalitet, format, do/don't — så att leverans 1 ser ut som leverans 50.",
      },
      {
        title: "2. Månadsplan",
        body: "Tillsammans planerar vi månadens hooks, kampanjer och cutdowns. Ni godkänner — vi producerar.",
      },
      {
        title: "3. Leverans + lärdomar",
        body: "Färdigt material i alla format ni behöver, plus en kort månadsrapport på vad som presterat.",
      },
    ],
    proof: [
      {
        client: "Bolt",
        videoId: "yvojXrb41Aw",
        tag: "Pulse · Performance",
        impact: "20-sekunders performance i 9:16 — paid social med tydligt produktbudskap.",
      },
      {
        client: "ALSO / Huawei",
        videoId: "HwWGVOl6dtg",
        tag: "Prime · B2B",
        impact: "Internationell brandfilm som översatte teknisk produkt till mänsklig historia.",
      },
    ],
    leadMagnet: {
      slug: "formatguide-2026",
      title: "Formatguide 2026",
      desc: "Vilket filmformat funkar var — feed, story, YouTube, webinars och paid social. Inkluderar längder, hooks och CTA-mönster.",
      cta: "Skicka mig guiden",
    },
    faq: [
      {
        q: "Hur funkar det med vår brandbook?",
        a: "Vi börjar varje samarbete med en onboarding där vi översätter er brandbook till film. Färg, typografi, ljud, tonalitet — allt baked in.",
      },
      {
        q: "Vad händer om vi behöver volym en månad och lite nästa?",
        a: "Pulse är designat för det. Bygg upp poäng vissa månader, lös ut dem när kampanjen drar igång. Inga öronmärkta timmar som brinner inne.",
      },
      {
        q: "Kan ni jobba med vår befintliga byrå?",
        a: "Ofta — vi blir produktionsmotorn medan byrån driver strategi. Vi konkurrerar inte om kundrelationen.",
      },
      {
        q: "Hur snabbt är 'snabbt'?",
        a: "Standard är 10–14 dagar brief→färdigt. Vid rush 3–5 dagar mot timeline-tillägg.",
      },
    ],
    heroVideoId: "HwWGVOl6dtg",
    stills: [
      "/prime-stills/also-fenny-1.jpg",
      "/prime-stills/bolt-fenny-1.jpg",
      "/prime-stills/elektronikbranschen-fenny-1.jpg",
      "/prime-stills/also-fenny-2.jpg",
      "/prime-stills/bolt-fenny-2.jpg",
      "/prime-stills/fenny-reklam-film-2.jpg",
      "/prime-stills/also-fenny-3.jpg",
      "/prime-stills/elektronikbranschen-fenny-2.jpg",
    ],
  },
  {
    slug: "byraer",
    to: "/for-vem/byraer",
    label: "Byråer",
    intro: "White-label produktionspartner för era kunder.",
    longIntro:
      "Ni levererar strategi och koncept — vi blir er produktionsmotor. Diskret, snabb, kvalitetssäkrad. Era kunder ser bara er; vi syns bara om ni vill det.",
    metric: "Marginaler kvar på rörligt — utan att hålla intern produktion",
    pains: [
      "Att hålla intern produktion är dyrt och svårt att skala",
      "Frilansare betyder olika kvalitet varje gång",
      "Ni behöver en partner som inte konkurrerar om kundrelationen",
    ],
    outcomes: [
      "Förutsägbara marginaler på rörligt utan egen produktion",
      "Samma team, samma kvalitet — varje gång",
      "White-label leveranser, fakturor i ert namn om ni vill",
    ],
    recommend: "Prime + Pulse hybrid",
    recommendHref: "/tjanster",
    quote: {
      text: "De levererar som ett internt team utan att kosta som ett.",
      who: "Creative director, fullservicebyrå",
    },
    processSteps: [
      {
        title: "1. NDA + partnerbrief",
        body: "Vi skriver under det ni behöver. Ni får en partnerkontakt och tydliga rutiner — inga överraskningar inför kund.",
      },
      {
        title: "2. White-label produktion",
        body: "Vi jobbar i er pipeline, ert namn, er tonalitet. Ni kan vara med eller låta oss driva — ni bestämmer.",
      },
      {
        title: "3. Leverans + faktura",
        body: "Färdigt material levererat i era mallar. Faktura till er, eller direkt till slutkund — ni väljer.",
      },
    ],
    proof: [
      {
        client: "ALSO / Huawei",
        videoId: "HwWGVOl6dtg",
        tag: "Prime · White-label",
        impact: "Internationell B2B-brandfilm levererad i partnerns flöde och namn.",
      },
      {
        client: "Garageportexperten",
        videoId: "9iiyNS4yNzA",
        tag: "Prime · Brand",
        impact: "Löpande Prime-spår där byrån drev strategi och vi producerade.",
      },
    ],
    leadMagnet: {
      slug: "byraer-white-label-kalkyl",
      title: "White-label kalkyl",
      desc: "Räkneark som visar marginaler, leveranstid och kapacitet när ni outsourcar produktion till oss vs håller internt.",
      cta: "Skicka mig kalkylen",
    },
    faq: [
      {
        q: "Konkurrerar ni om kundrelationen?",
        a: "Nej. Vi går aldrig direkt till era kunder. Allt går via er, alltid.",
      },
      {
        q: "Vad gäller kring IP och rättigheter?",
        a: "All färdig film är er kunds — vi tar ingen royalty. Ni reglerar rättigheter med slutkund som vanligt.",
      },
      {
        q: "Kan ni fakturera direkt till vår kund?",
        a: "Ja, i ert namn eller med oss som underleverantör. Båda fungerar.",
      },
      {
        q: "Hur snabb är onboardingen?",
        a: "Första gemensamma leverans typiskt 7–10 dagar från NDA. Vi har gjort det här åt 20+ byråer.",
      },
    ],
    heroVideoId: "HwWGVOl6dtg",
    stills: [
      "/prime-stills/also-fenny-2.jpg",
      "/prime-stills/garageportexperten-fenny-3.jpg",
      "/prime-stills/bolt-fenny-3.jpg",
      "/prime-stills/also-fenny-1.jpg",
      "/prime-stills/garageportexperten-fenny-7.jpg",
      "/prime-stills/elektronikbranschen-fenny-3.jpg",
      "/prime-stills/also-fenny-3.jpg",
      "/prime-stills/garageportexperten-fenny-8.jpg",
    ],
  },
  {
    slug: "forbund",
    to: "/for-vem/forbund",
    label: "Förbund & organisationer",
    intro: "Berätta medlemsnyttan — tydligt, ofta, professionellt.",
    longIntro:
      "Era medlemmar behöver påminnas om varför de är med, och nya medlemmar behöver förstå värdet på 30 sekunder. Vi paketerar det löpande så ni slipper börja om varje år.",
    metric: "Ett gemensamt visuellt språk för hela organisationen — året om",
    pains: [
      "Medlemsnyttan känns självklar internt men oklar utåt",
      "Kongresser, event och kampanjer kräver film i puckar",
      "Begränsad budget — ändå höga kvalitetskrav",
    ],
    outcomes: [
      "Återanvändbart material som lever hela året",
      "Tydlig rekryteringskommunikation som ökar nya medlemmar",
      "Event och kongresser som lever vidare digitalt",
    ],
    recommend: "Pulse Core + Prime vid event",
    recommendHref: "/pulse",
    quote: {
      text: "Vi har äntligen ett gemensamt visuellt språk i hela organisationen.",
      who: "Kommunikationschef, branschförbund",
    },
    processSteps: [
      {
        title: "1. Årshjul-workshop",
        body: "Vi mappar era nyckelmoment — kongress, kampanjvågor, medlemsvärvning — och bygger en visuell plan för hela året.",
      },
      {
        title: "2. Löpande produktion",
        body: "Pulse-leverans månadsvis: medlemsröster, kampanjklipp, rekryteringsfilm. Allt i en konsekvent tonalitet.",
      },
      {
        title: "3. Event + after-life",
        body: "Vi filmar kongressen/eventet och förvaltar materialet hela året så det jobbar vidare i sociala kanaler.",
      },
    ],
    proof: [
      {
        client: "Garageportexperten",
        videoId: "9iiyNS4yNzA",
        tag: "Prime · Brand",
        impact: "Brand storytelling som blivit ett gemensamt visuellt språk över avdelningar.",
      },
      {
        client: "ALSO / Huawei",
        videoId: "HwWGVOl6dtg",
        tag: "Prime · Storytelling",
        impact: "Storytelling som översätter teknisk medlemsnytta till mänskliga berättelser.",
      },
    ],
    leadMagnet: {
      slug: "forbund-arshjul",
      title: "Medlemskommunikations-årshjul",
      desc: "En mall för 12 månader av film: medlemsvärvning, kongress, kampanjvåg och påminnelsemoment — med rekommenderade format.",
      cta: "Skicka mig årshjulet",
    },
    faq: [
      {
        q: "Vi har begränsad budget — hur funkar Pulse?",
        a: "Pulse Core är designat för organisationer som behöver konsekvent produktion utan att överraskas av offerter. Fast månadsavgift, planerbart.",
      },
      {
        q: "Kan ni filma vår kongress?",
        a: "Ja. Det blir typiskt en Prime-produktion ovanpå löpande Pulse — så att materialet lever vidare hela året, inte bara på eventdagen.",
      },
      {
        q: "Hur säkrar ni att budskapet sitter?",
        a: "Strategimöte, manuscheck och godkännande i tre steg. Ni signar innan vi rullar kamera och innan vi levererar.",
      },
      {
        q: "Får alla våra avdelningar använda materialet?",
        a: "Ja — ni äger materialet och kan distribuera internt fritt.",
      },
    ],
    heroVideoId: "GPm8ubB79DM",
    stills: [
      "/prime-stills/elektronikbranschen-fenny-1.jpg",
      "/prime-stills/svensk-radonfoorening-1.jpg",
      "/prime-stills/garageportexperten-fenny-4.jpg",
      "/prime-stills/elektronikbranschen-fenny-2.jpg",
      "/prime-stills/garageportexperten-fenny-9.jpg",
      "/prime-stills/fenny-reklam-film-3.jpg",
      "/prime-stills/elektronikbranschen-fenny-3.jpg",
      "/prime-stills/garageportexperten-fenny-1.jpg",
    ],
  },
  {
    slug: "some-team",
    to: "/for-vem/some-team",
    label: "SoMe-team",
    intro: "Konstant flöde av klipp som presterar i feeden.",
    longIntro:
      "Ni vet vad som funkar i feeden — ni behöver bara fler råvaror att redigera, posta och boosta. Vi blir er content-motor som matar er kalender utan att tappa kvalitet eller tonalitet.",
    metric: "3× publiceringstakt utan att anställa — med mätbar performance per format",
    pains: [
      "Algoritmer kräver volym — ni hinner inte producera tillräckligt",
      "Frilansare ger inkonsekvent kvalitet",
      "Strateg blir producent blir redigerare — fokus splittras",
    ],
    outcomes: [
      "Veckovisa leveranser anpassade per plattform",
      "Hooks, varianter och cutdowns ingår — inte tilläggsfaktura",
      "Mätbar performance: views, retention, CTR per format",
    ],
    recommend: "Pulse Pro",
    recommendHref: "/pulse",
    quote: {
      text: "Vi tredubblade publiceringstakten utan att anställa.",
      who: "Social media lead, retailvarumärke",
    },
    processSteps: [
      {
        title: "1. Plattformsmix",
        body: "Vi mappar var ni vill växa — TikTok, Reels, Shorts, LinkedIn — och vilka format som presterar i varje feed.",
      },
      {
        title: "2. Veckovisa leveranser",
        body: "Klipp varje vecka i rätt format. Hooks, cutdowns och varianter ingår — inte 'extra'.",
      },
      {
        title: "3. Performance-loop",
        body: "Vi tittar tillsammans på vad som retentionar och vad som dör i feeden. Nästa veckas plan justeras därefter.",
      },
    ],
    proof: [
      {
        client: "Bolt",
        videoId: "yvojXrb41Aw",
        tag: "Pulse · Social",
        impact: "20-sek paid social i 9:16 — produktbudskap som faktiskt stoppar tummen.",
      },
      {
        client: "Garageportexperten",
        videoId: "9iiyNS4yNzA",
        tag: "Prime · Brand-to-social",
        impact: "Brandfilm omvandlad till veckovis flöde av sociala klipp och cutdowns.",
      },
    ],
    leadMagnet: {
      slug: "some-hooks-2026",
      title: "Hooks som funkar 2026",
      desc: "25 hook-mönster vi sett retentionar i Reels, TikTok och Shorts — med exempel på copy, beat och CTA.",
      cta: "Skicka mig hook-paketet",
    },
    faq: [
      {
        q: "Funkar ni med vårt befintliga schema?",
        a: "Ja — vi anpassar leverans till er publiceringskalender, inte tvärtom.",
      },
      {
        q: "Hur många klipp per vecka?",
        a: "Beroende på paket: typiskt 3–8 färdiga klipp i flera plattformsvarianter per vecka på Pulse Pro.",
      },
      {
        q: "Kan vi äga råmaterialet?",
        a: "Ja, ni får tillgång till råmaterial och projektfiler om ni vill klippa egna varianter senare.",
      },
      {
        q: "Hur mäter ni performance?",
        a: "Vi rapporterar på views, retention, CTR och saves per format så ni ser vad som presterar — inte bara hur mycket vi har levererat.",
      },
    ],
    heroVideoId: "BzHML38R2bs",
    stills: [
      "/prime-stills/pricka-fenny-1.jpg",
      "/prime-stills/bolt-fenny-4.jpg",
      "/prime-stills/fenny-reklam-film-4.jpg",
      "/prime-stills/pricka-fenny-2.jpg",
      "/prime-stills/bolt-fenny-5.jpg",
      "/prime-stills/fenny-reklam-film-5.jpg",
      "/prime-stills/wimeck-fenny-1.jpg",
      "/prime-stills/fenny-reklam-film-6.jpg",
    ],
  },
];

export const getAudience = (slug: string) =>
  audiences.find((a) => a.slug === slug);
